import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  ChevronRight,
  CircleHelp,
  Keyboard,
  Lock,
  LogOut,
  MessageSquare,
  Search,
  Shield,
  UserRound,
} from "lucide-react";
import { ROUTES } from "@/constants/routes";
import { queryKeys } from "@/constants/queryKeys";
import { cn } from "@/lib/cn";
import { initialsFromName } from "@/lib/media";
import { fetchProfile } from "@/services/api";
import { normalizeProfile, useAuthStore } from "@/store";
import { FloatingDialerButton } from "@/components/dialer/FloatingDialerButton";
import { AccountSection } from "./sections/AccountSection";
import { ChatsSection } from "./sections/ChatsSection";
import { HelpSection } from "./sections/HelpSection";
import { NotificationsSection } from "./sections/NotificationsSection";
import { PrivacySection } from "./sections/PrivacySection";
import { ProfileSection } from "./sections/ProfileSection";
import { ShortcutsSection } from "./sections/ShortcutsSection";

type SettingsSection =
  | "profile"
  | "account"
  | "privacy"
  | "chats"
  | "notifications"
  | "shortcuts"
  | "help";

const menuItems: Array<{
  id: SettingsSection;
  title: string;
  description: string;
  icon: typeof UserRound;
}> = [
  {
    id: "profile",
    title: "Profile",
    description: "Name, profile picture, username",
    icon: UserRound,
  },
  {
    id: "account",
    title: "Account",
    description: "Security notifications, account info",
    icon: Lock,
  },
  {
    id: "privacy",
    title: "Privacy",
    description: "Blocked contacts, disappearing messages",
    icon: Shield,
  },
  {
    id: "chats",
    title: "Chats",
    description: "Theme, wallpaper, chat settings",
    icon: MessageSquare,
  },
  {
    id: "notifications",
    title: "Notifications",
    description: "Messages, groups, sounds",
    icon: Bell,
  },
  {
    id: "shortcuts",
    title: "Keyboard shortcuts",
    description: "Quick actions",
    icon: Keyboard,
  },
  {
    id: "help",
    title: "Help and feedback",
    description: "Help centre, contact us, privacy policy",
    icon: CircleHelp,
  },
];

function ProfileFace({
  name,
  src,
  className,
}: {
  name: string;
  src?: string | null;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;

  return (
    <div
      className={cn(
        "inline-flex items-center justify-center overflow-hidden rounded-full bg-papaya/15 font-semibold text-papaya-deep",
        className,
      )}
    >
      {showImage ? (
        <img
          src={src!}
          alt={name}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        initialsFromName(name)
      )}
    </div>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const setProfile = useAuthStore((s) => s.setProfile);
  const accessToken = useAuthStore((s) => s.accessToken);
  const logout = useAuthStore((s) => s.logout);
  const [section, setSection] = useState<SettingsSection>("profile");
  const [query, setQuery] = useState("");

  const profileQuery = useQuery({
    queryKey: queryKeys.profile,
    queryFn: fetchProfile,
    enabled: Boolean(accessToken),
  });

  useEffect(() => {
    if (profileQuery.data) {
      setProfile(profileQuery.data);
    }
  }, [profileQuery.data, setProfile]);

  const liveProfileRaw = profileQuery.data ?? profile;
  const liveProfile = liveProfileRaw ? normalizeProfile(liveProfileRaw) : null;
  const name = liveProfile?.name || user?.name || "Papaya Agent";
  const email = liveProfile?.email || user?.email || "";
  const avatar = liveProfile?.profile_pic || user?.profilePic || null;
  const status = liveProfile?.current_status || user?.currentStatus || null;

  const filteredMenu = menuItems.filter((item) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      item.title.toLowerCase().includes(q) ||
      item.description.toLowerCase().includes(q)
    );
  });

  return (
    <div className="relative flex h-full min-h-0 w-full overflow-hidden rounded-[6px] border border-border bg-surface shadow-[0_12px_36px_rgba(65,33,10,0.08)] dark:shadow-[0_12px_36px_rgba(0,0,0,0.35)]">
      <aside className="flex h-full w-full flex-col lg:w-[300px] lg:max-w-[300px] lg:shrink-0 lg:border-r lg:border-border">
        <div className="flex items-center gap-3 px-4 pt-4 pb-2">
          <ProfileFace name={name} src={avatar} className="h-9 w-9 text-sm font-bold" />
          <h2 className="text-xl font-semibold text-fg">Settings</h2>
        </div>

        <div className="px-3 pb-3">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-fg-subtle" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search"
              className="h-10 w-full rounded-full border border-transparent bg-surface-muted pr-3 pl-9 text-sm text-fg outline-none placeholder:text-fg-subtle focus-visible:ring-2 focus-visible:ring-ring/30"
            />
          </div>
        </div>

        <div className="px-4 pb-4">
          <div className="relative mx-auto w-fit">
            {status ? (
              <span className="absolute -top-2 left-1/2 z-10 -translate-x-1/2 rounded-full bg-surface-muted px-2.5 py-1 text-[10px] font-medium text-fg-muted shadow-sm">
                {status}
              </span>
            ) : null}
            <ProfileFace
              name={name}
              src={avatar}
              className="mx-auto h-28 w-28 border-4 border-surface text-2xl shadow-sm"
            />
          </div>
          <p className="mt-3 text-center text-base font-semibold text-fg">{name}</p>
          <p className="text-center text-sm text-fg-muted">{email}</p>
          {liveProfile?.organization?.name ? (
            <p className="mt-1 text-center text-xs text-fg-subtle">
              {liveProfile.organization.name}
            </p>
          ) : null}
        </div>

        <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {filteredMenu.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSection(item.id)}
              className={cn(
                "flex w-full items-center gap-3 rounded-[6px] px-3 py-3 text-left transition-colors",
                section === item.id ? "bg-surface-muted" : "hover:bg-surface-muted/70",
              )}
            >
              <item.icon className="h-5 w-5 shrink-0 text-fg-muted" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-fg">{item.title}</span>
                <span className="block truncate text-xs text-fg-muted">
                  {item.description}
                </span>
              </span>
              <ChevronRight className="h-4 w-4 text-fg-subtle" />
            </button>
          ))}
        </nav>

        <div className="border-t border-border p-2">
          <button
            type="button"
            onClick={() => {
              logout({ redirect: false });
              navigate(ROUTES.login, { replace: true });
            }}
            className="flex w-full items-center gap-3 rounded-[6px] px-3 py-3 text-left text-danger hover:bg-danger/10"
          >
            <LogOut className="h-5 w-5" />
            <span className="text-sm font-semibold">Log out</span>
          </button>
        </div>
      </aside>

      <section className="relative hidden min-w-0 flex-1 overflow-hidden lg:block">
        {section === "profile" ? <ProfileSection profile={liveProfile} /> : null}
        {section === "account" ? <AccountSection profile={liveProfile} /> : null}
        {section === "privacy" ? <PrivacySection /> : null}
        {section === "chats" ? <ChatsSection /> : null}
        {section === "notifications" ? <NotificationsSection /> : null}
        {section === "shortcuts" ? <ShortcutsSection /> : null}
        {section === "help" ? <HelpSection /> : null}
        <FloatingDialerButton />
      </section>

      <FloatingDialerButton className="lg:hidden" />
    </div>
  );
}
